import React from "react"
import debounce from "debounce-promise"
import { updateWebsite } from "context/actions"
import * as api from "api/websites"
import { HStack, Text } from "@chakra-ui/react"
import { eventWebPagePath } from "utilities/routes"
import { useHistory } from "react-router-dom"
import { useQueryClient } from "react-query"
import { useCurrentEvent } from "queries/events"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"

const testWebsiteUrl = async (urlPath: string) => {
  const errors: {urlPath?: string} = {}

  if (urlPath.length < 3) {
    return "Please enter at least 3 characters"
  }

  await api.validateWebsiteUrlPath(urlPath)
    .then((response) => {
      if (!response.data.valid) {
        errors.urlPath = response.data.error.userMessage
      }
    })
    .catch(() => {
      errors.urlPath = "Something went wrong."
    })

  return errors.urlPath
}

const debouncedTestWebsiteUrl = debounce(testWebsiteUrl, 250)

type Props = {
  eventId: string
  urlPath: string
  onHide: () => void
  redirectPath?: string
}

const WebPageURL = ({
  eventId, urlPath, onHide, redirectPath,
}: Props) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const { data: event } = useCurrentEvent()
  const origin = event?.urls ? new URL(event.urls.root).origin : ""

  const onSubmit = (values) => {
    const resetAndHide = () => {
      onHide()
    }

    // Use provided redirectPath or default to web page settings
    const finalRedirectPath = redirectPath || eventWebPagePath(eventId)

    return api.putWebsite(eventId, values)
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        history.push(finalRedirectPath)
        resetAndHide()
      })
      .catch(() => {
        // API service automatically alerts the user, so code can continue to run,
        // but we don't want to close the modal
      })
  }

  const initialValues = {
    urlPath: urlPath || "",
  }

  return (
    <ModalForm
      title="Page URL"
      onClose={onHide}
      initialValues={initialValues}
      onSubmit={onSubmit}
      size="lg"
    >
      {({ control, errors }) => {
        const urlPathMessage = errors.urlPath?.message

        return (
          <>
            <Text fontSize="md" mb={5}>Set a short, custom link for your event page.</Text>
            <HStack align="baseline">
              <Text fontSize="xl" fontWeight="semibold">{origin}/</Text>
              <TextField
                name="urlPath"
                control={control}
                variant="outline"
                size="xl"
                fontWeight="semibold"
                error={!!urlPathMessage}
                errorText={urlPathMessage}
                validate={debouncedTestWebsiteUrl}
              />
            </HStack>
          </>
        )
      }}
    </ModalForm>
  )
}

export default WebPageURL
